/**
 * 1.8.0: the editor's rights (only while rights management is on): the top-bar pill ("Rights: 2 blocked") that opens
 * the Rights side tab; the tab with the combined state, the earliest end and the latest embargo, the sources with
 * their tracks, programme timecodes, time used, holder, status, restrictions and credit, the credits with Copy and
 * Download rights report (CSV); and the toast when a withdrawn clip is added. The editor itself never blocks.
 */
import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Download } from 'lucide-react';
import { RIGHTS_STATE_LABELS, rightsColor, ticksToTimecodeString, type RightsPolicy, type SequenceDocument } from '@prism/contracts';
import { rightsReportUrl, type SequenceSource } from '../../api/hooks';
import { RightsMark } from '../../components/RightsMark';
import { TableScroll } from '../../components/TableScroll';
import { toast } from '../../components/Toasts';
import { duration } from '../../lib/format';
import { rightsInstant, rightsStateText, sequenceRightsPill } from '../../lib/rights';
import { combinedOf, sequenceRightsRows, withdrawnClipIds } from './sequenceRights';

/** The top-bar pill; nothing while no source carries rights (rights off). */
export function SequenceRightsPill({ sources, policy, onOpen }: { sources: readonly SequenceSource[]; policy: RightsPolicy | undefined; onOpen: () => void }) {
  const pill = sequenceRightsPill(sources.flatMap((s) => (s.rights ? [s.rights] : [])), policy);
  if (!pill) return null;
  return (
    <button type="button" className={`pill rights-pill ${pill.tone}`} data-testid="rights-pill" data-state={pill.state} onClick={onOpen} title="Open the Rights tab">
      © {pill.text}
    </button>
  );
}

export function SequenceRightsTab({ doc, sources, sequenceId }: { doc: SequenceDocument; sources: readonly SequenceSource[]; sequenceId: string }) {
  const rows = useMemo(() => sequenceRightsRows(doc, sources), [doc, sources]);
  const combined = combinedOf(rows);
  const tc = (t: number) => ticksToTimecodeString(t, doc.frameRate);
  const copyCredits = async () => {
    try {
      await navigator.clipboard.writeText(combined?.credits.join('\n') ?? '');
      toast('Credits copied');
    } catch {
      toast('Select the credits and copy them', 'error');
    }
  };
  return (
    <div className="rights-tab" data-testid="rights-tab">
      {combined ? (
        <div className={`rights-summary ${rightsColor(combined)}`} data-testid="rights-summary" data-state={combined.state}>
          <strong>© {rightsStateText(combined)}</strong>
          <span className="muted">
            {[combined.until && `Earliest end ${combined.until}`, combined.from && `latest embargo until ${rightsInstant(combined.from)}`].filter(Boolean).join(' · ') || 'No end or embargo'}
          </span>
          <span className="faint">{Object.entries(combined.counts).filter(([, n]) => n > 0).map(([s, n]) => `${n} ${RIGHTS_STATE_LABELS[s as keyof typeof RIGHTS_STATE_LABELS].toLowerCase()}`).join(' · ')}</span>
        </div>
      ) : <p className="muted" style={{ margin: 0 }}>No sources yet.</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <a className="btn sm" href={rightsReportUrl.sequence(sequenceId)} download data-testid="rights-report-download"><Download size={13} /> Download rights report (CSV)</a>
      </div>
      {rows.length > 0 && (
        <TableScroll>
          <table className="table compact rights-sources" data-testid="rights-sources">
            <thead><tr><th>Source</th><th>Tracks</th><th>Programme</th><th>Used</th><th>Holder</th><th>Status</th><th>Restrictions</th><th>Credit</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.assetId} data-testid="rights-source" data-asset-id={r.assetId} data-state={r.rights?.state}>
                  <td className="wrap" style={{ minWidth: 120 }}><Link to={`/assets/${r.assetId}?tab=rights`}>{r.title}</Link><RightsMark rights={r.rights} /></td>
                  <td className="nowrap">{r.tracks.join(', ')}</td>
                  <td className="mono wrap" style={{ fontSize: 11 }}>{r.uses.map((u, i) => <div key={i}>{tc(u.recordInTicks)}–{tc(u.recordOutTicks)}</div>)}</td>
                  <td className="mono nowrap">{duration(r.usedTicks)}</td>
                  <td className="wrap">{r.rights?.holder ?? <span className="faint">—</span>}</td>
                  <td className="nowrap">{r.rights ? rightsStateText(r.rights) : <span className="faint">—</span>}</td>
                  <td className="wrap">{r.rights?.restrictions ?? <span className="faint">—</span>}</td>
                  <td className="wrap">{r.rights?.credit ?? <span className="faint">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {combined && combined.credits.length > 0 && (
        <div className="rights-credits" data-testid="rights-credits">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <p className="section-label" style={{ margin: 0, flex: 1 }}>Credits</p>
            <button className="btn ghost sm" onClick={() => void copyCredits()} data-testid="rights-credits-copy"><Copy size={12} /> Copy</button>
          </div>
          <ul>{combined.credits.map((c) => <li key={c}>{c}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/** A toast when a clip whose source is withdrawn is added (the editor never blocks; exports will). */
export function useWithdrawnClipToast(doc: SequenceDocument, sources: ReadonlyMap<string, SequenceSource>) {
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    const now = withdrawnClipIds(doc, sources);
    // The first answer with sources is what the sequence already had.
    if (seen.current === null) {
      if (sources.size) seen.current = now;
      return;
    }
    const added = [...now].filter((id) => !seen.current!.has(id));
    seen.current = now;
    if (added.length) toast(added.length === 1 ? 'This clip has been withdrawn and must not be used: exports of this sequence are blocked' : `${added.length} withdrawn clips added: exports of this sequence are blocked`, 'error');
  }, [doc, sources]);
}
